import {
  CollectionCompatibilityMode,
  CursorPage,
  CollectionSession,
  CollectionSummary,
  InventoryAuthority,
} from '@qkindex/contracts';
import {
  CollectionRepository,
  isExpectedInaccessibleCollectionError,
} from '../../repositories/collectionRepository';

export const ACTIVE_COLLECTION_PREFERENCE_PREFIX = 'qkindex.activeCollection.';

export type CollectionResolutionStatus =
  'idle' | 'loading' | 'ready' | 'empty' | 'revoked' | 'unavailable' | 'error';

export interface CollectionResolution {
  status: CollectionResolutionStatus;
  session: CollectionSession;
  authority?: InventoryAuthority;
}

export const collectionCompatibilityMode = (): CollectionCompatibilityMode => {
  return 'collection-only';
};

export const collectionWorkflowsReady = (): boolean => true;

export const activeCollectionPreferenceKey = (userId: string): string =>
  `${ACTIVE_COLLECTION_PREFERENCE_PREFIX}${userId}`;

const ready = (
  userId: string,
  availableCollections: CursorPage<CollectionSummary>,
  selected: CollectionSummary,
  compatibilityMode: CollectionCompatibilityMode,
): CollectionResolution => ({
  status: 'ready',
  session: {
    userId,
    availableCollections,
    activeCollectionId: selected.collectionId,
    activeRole: selected.role,
    membershipVersion: selected.membershipVersion,
  },
  ...(selected.lifecycle === 'deleted'
    ? {}
    : {
        authority: {
          userId,
          collectionId: selected.collectionId,
          compatibilityMode,
        },
      }),
});

const withoutActiveCollection = (
  userId: string,
  status: Exclude<CollectionResolutionStatus, 'idle' | 'loading' | 'ready'>,
  availableCollections: CursorPage<CollectionSummary> = { items: [] },
): CollectionResolution => ({
  status,
  session: { userId, availableCollections },
});

const usableCollections = (session: CollectionSession): CollectionSummary[] =>
  session.availableCollections.items.filter(
    ({ lifecycle }) =>
      lifecycle === 'active' || lifecycle === 'transfer-pending',
  );

export const resolveCollectionSession = async (
  userId: string,
  repository: CollectionRepository,
  compatibilityMode: CollectionCompatibilityMode,
  savedCollectionId = localStorage.getItem(
    activeCollectionPreferenceKey(userId),
  ),
): Promise<CollectionResolution> => {
  if (compatibilityMode !== 'collection-only')
    return withoutActiveCollection(userId, 'unavailable');
  if (!collectionWorkflowsReady()) {
    return withoutActiveCollection(userId, 'unavailable');
  }

  let loaded: CollectionSession;
  try {
    loaded = await repository.loadSession(userId);
    if (
      !loaded?.availableCollections ||
      !Array.isArray(loaded.availableCollections.items)
    ) {
      throw new Error('Collection session response is invalid.');
    }
    // Membership pages can contain only inaccessible/deleting collections.
    // Exhaust those pages before declaring that the account has no collection.
    while (
      usableCollections(loaded).length === 0 &&
      loaded.availableCollections.nextCursor
    ) {
      loaded = await repository.loadSession(
        userId,
        20,
        loaded.availableCollections.nextCursor,
      );
    }
  } catch {
    return withoutActiveCollection(userId, 'error');
  }

  const available = usableCollections(loaded);
  const availablePage: CursorPage<CollectionSummary> = {
    items: available,
    ...(loaded.availableCollections.nextCursor
      ? { nextCursor: loaded.availableCollections.nextCursor }
      : {}),
  };
  const saved = savedCollectionId
    ? available.find(({ collectionId }) => collectionId === savedCollectionId)
    : undefined;
  if (saved) return ready(userId, availablePage, saved, compatibilityMode);

  if (savedCollectionId) {
    try {
      const membership = await repository.getMembership(
        savedCollectionId,
        userId,
      );
      if (membership?.status === 'active') {
        const collection = await repository.get(savedCollectionId);
        if (
          collection &&
          ['active', 'transfer-pending'].includes(collection.lifecycle)
        ) {
          const authoritative: CollectionSummary = {
            collectionId: savedCollectionId,
            name: collection.name,
            role: membership.role,
            lifecycle: collection.lifecycle,
            lifecycleVersion: collection.lifecycleVersion,
            membershipVersion: membership.membershipVersion,
            ownerUserId: collection.ownerUserId,
            ...(collection.gps ? { gps: collection.gps } : {}),
          };
          return ready(
            userId,
            {
              items: [
                authoritative,
                ...available.filter(
                  ({ collectionId }) => collectionId !== savedCollectionId,
                ),
              ],
              ...(availablePage.nextCursor
                ? { nextCursor: availablePage.nextCursor }
                : {}),
            },
            authoritative,
            compatibilityMode,
          );
        }
      }
    } catch (error) {
      if (
        !isExpectedInaccessibleCollectionError(error) &&
        available.length === 0
      ) {
        return withoutActiveCollection(userId, 'error', availablePage);
      }
    }
    localStorage.removeItem(activeCollectionPreferenceKey(userId));
  }

  if (available.length > 0) {
    const selected = available[0];
    localStorage.setItem(
      activeCollectionPreferenceKey(userId),
      selected.collectionId,
    );
    return ready(userId, availablePage, selected, compatibilityMode);
  }
  return withoutActiveCollection(
    userId,
    availablePage.nextCursor ? 'unavailable' : 'empty',
    availablePage,
  );
};

export const selectActiveCollection = async (
  current: CollectionResolution,
  collectionId: string,
  repository: CollectionRepository,
  compatibilityMode: CollectionCompatibilityMode,
): Promise<CollectionResolution> => {
  const userId = current.session.userId;
  if (compatibilityMode !== 'collection-only')
    return withoutActiveCollection(userId, 'unavailable');
  if (!collectionWorkflowsReady()) {
    return withoutActiveCollection(userId, 'unavailable');
  }

  const selected = current.session.availableCollections.items.find(
    (candidate) => candidate.collectionId === collectionId,
  );
  try {
    const [membership, collection] = await Promise.all([
      repository.getMembership(collectionId, userId),
      repository.get(collectionId),
    ]);
    if (!membership || membership.status === 'revoked') {
      localStorage.removeItem(activeCollectionPreferenceKey(userId));
      return withoutActiveCollection(
        userId,
        'revoked',
        current.session.availableCollections,
      );
    }
    if (
      membership.collectionId !== collectionId ||
      membership.userId !== userId ||
      !collection ||
      !['active', 'transfer-pending'].includes(collection.lifecycle)
    ) {
      return withoutActiveCollection(
        userId,
        'unavailable',
        current.session.availableCollections,
      );
    }
    const authoritative: CollectionSummary = {
      collectionId,
      name: collection.name,
      role: membership.role,
      lifecycle: collection.lifecycle,
      lifecycleVersion: collection.lifecycleVersion,
      membershipVersion: membership.membershipVersion,
      ownerUserId: collection.ownerUserId,
      ...(collection.gps ? { gps: collection.gps } : {}),
      ...(selected?.ownerUsername
        ? { ownerUsername: selected.ownerUsername }
        : {}),
    };
    localStorage.setItem(activeCollectionPreferenceKey(userId), collectionId);
    return ready(
      userId,
      {
        items: current.session.availableCollections.items
          .map((candidate) =>
            candidate.collectionId === collectionId ? authoritative : candidate,
          )
          .concat(selected ? [] : [authoritative]),
        ...(current.session.availableCollections.nextCursor
          ? { nextCursor: current.session.availableCollections.nextCursor }
          : {}),
      },
      authoritative,
      compatibilityMode,
    );
  } catch {
    return withoutActiveCollection(
      userId,
      'error',
      current.session.availableCollections,
    );
  }
};

export const loadMoreCollectionSession = async (
  current: CollectionResolution,
  repository: CollectionRepository,
  compatibilityMode: CollectionCompatibilityMode,
): Promise<CollectionResolution> => {
  const cursor = current.session.availableCollections.nextCursor;
  if (
    !cursor ||
    compatibilityMode !== 'collection-only' ||
    !collectionWorkflowsReady()
  ) {
    return current;
  }

  const loaded = await repository.loadSession(
    current.session.userId,
    20,
    cursor,
  );
  const merged = new Map(
    current.session.availableCollections.items.map(
      (collection) => [collection.collectionId, collection] as const,
    ),
  );
  usableCollections(loaded).forEach((collection) => {
    merged.set(collection.collectionId, collection);
  });
  const availableCollections: CursorPage<CollectionSummary> = {
    items: [...merged.values()],
    ...(loaded.availableCollections.nextCursor
      ? { nextCursor: loaded.availableCollections.nextCursor }
      : {}),
  };
  const selected = current.session.activeCollectionId
    ? availableCollections.items.find(
        ({ collectionId }) =>
          collectionId === current.session.activeCollectionId,
      )
    : undefined;
  if (selected) {
    return ready(
      current.session.userId,
      availableCollections,
      selected,
      compatibilityMode,
    );
  }
  if (availableCollections.items.length > 0) {
    const first = availableCollections.items[0];
    localStorage.setItem(
      activeCollectionPreferenceKey(current.session.userId),
      first.collectionId,
    );
    return ready(
      current.session.userId,
      availableCollections,
      first,
      compatibilityMode,
    );
  }
  return withoutActiveCollection(
    current.session.userId,
    current.status === 'ready' ||
      current.status === 'idle' ||
      current.status === 'loading'
      ? 'unavailable'
      : current.status,
    availableCollections,
  );
};
